import React, { FC, useMemo } from 'react';
import { Box, Flex, Grid, HStack, Text, VStack } from '@chakra-ui/react';
import clsx from 'clsx';
import s from '@app/screens/positions/components/PositionsList/PositionsList.module.scss';
import { PoolTokensCell } from '@app/screens/pools/components/PoolTokensCell';
import { toOptionalFixed } from '@app/helpers/format';
import { TokenIcon } from '@app/components/TokenIcon';
import { useSinglePoolQuery } from '@app/hooks/v3/useSingelPoolQuery';
import { useNativePriceQuery } from '@app/hooks/v3/useNativePriceQuery';
import { Icon } from '@app/components/Icon';
import { Tooltip } from '@app/components/Tooltip';
import { isOldWethPool } from '@app/helpers/positions';
import { getCurrencySymbol } from '@app/helpers/currency';
import { Token } from '@app/types/token';
import { BigNumber, ethers } from 'ethers';
import { Vault } from '@app/hooks/plugins/useVaults';
import { VaultPositionControls } from '../VaultPositionControls';
import { formatUSD } from '@app/helpers/formatUSD';
import { useVaultPositionFees } from '@app/hooks/plugins/useVaultPositionFees';
import Image from 'next/image';
import { IoChevronForward } from 'react-icons/io5';
import { getStrategyAcronim } from '@app/helpers/vaults';
import { useMedia } from 'react-use';
import { AmountSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/AmountSection';
import { CompositionSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/CompositionSection';
import { APRSection } from '@app/screens/positions/components/SpNftPositionListRowItem/components/APRSection';
import { useFarmBaseApr } from '@app/hooks/plugins/useFarmBaseApr';
import { StrategySection } from '@app/components/StrategySection';

interface Props {
  data: {
    apr: number;
    totalAmounts: [BigNumber, BigNumber] | undefined;
    totalSupply: BigNumber | undefined;
    balance: BigNumber | undefined;
    token0: BigNumber | undefined;
    token1: BigNumber | undefined;
    vault: Vault;
  };
}

export const VaultsPositionsListRow: FC<Props> = ({ data }) => {
  const isMobile = useMedia('(max-width: 960px)', false);
  const { data: bundle } = useNativePriceQuery();
  const { data: poolInfo } = useSinglePoolQuery({
    poolId: data.vault.pool
  });

  const isOldWethPosition = isOldWethPool(
    poolInfo?.token0.id,
    poolInfo?.token1.id
  );

  const formattedPosition = useMemo(() => {
    if (!poolInfo || !bundle) {
      return;
    }

    const amount0 = data.token0
      ? ethers.utils.formatUnits(data.token0, poolInfo.token0.decimals)
      : 0;

    const amount1 = data.token1
      ? ethers.utils.formatUnits(data.token1, poolInfo.token1.decimals)
      : 0;

    const usdAmount0 =
      +poolInfo.token0.derivedMatic *
      +bundle.maticPriceUSD *
      +amount0.toString();

    const usdAmount1 =
      +poolInfo.token1.derivedMatic *
      +bundle.maticPriceUSD *
      +amount1.toString();

    return {
      amount0,
      amount1,
      usdAmount0,
      usdAmount1,
      balance: data.balance
        ? +ethers.utils.formatUnits(data.balance, data.vault.lpToken.decimals)
        : null,
      balanceUsd: data.balance
        ? +ethers.utils.formatUnits(data.balance, data.vault.lpToken.decimals) *
          data.vault.lpToken.lpTokenUSD
        : null
    };
  }, [
    bundle,
    data.balance,
    data.token0,
    data.token1,
    data.vault.lpToken.decimals,
    data.vault.lpToken.lpTokenUSD,
    poolInfo
  ]);

  function renderContent() {
    if (!poolInfo) {
      return;
    }

    return (
      <>
        {isMobile ? (
          <VStack gap={3} width="100%">
            <HStack
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <PoolTokensCell
                token0={poolInfo.token0}
                token1={poolInfo.token1}
                version={4}
              />
              {isOldWethPosition && (
                <Tooltip
                  className={s.transferTooltip}
                  placement="bottom-start"
                  tooltip="weETH has upgraded their token standard, which requires swapping from the old to new weETH.
Please select transfer to automatically swap and create position in the new pool."
                >
                  <Icon
                    name="infoCircle"
                    style={{ color: '#eed202', marginLeft: '8px' }}
                  />
                </Tooltip>
              )}
            </HStack>
            <HStack
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Text fontSize="14px" color="gray.4">
                Strategy
              </Text>
              <StrategySection vault={data.vault} />
            </HStack>
            <HStack
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Text fontSize="14px" color="gray.4">
                Amount
              </Text>
              <AmountSection
                amount={formattedPosition?.balance ?? 0}
                usdAmount={formattedPosition?.balanceUsd ?? 0}
              />
            </HStack>
            <HStack
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Text fontSize="14px" color="gray.4">
                Composition
              </Text>
              <CompositionSection
                amount0={formattedPosition?.amount0 ?? ''}
                amount1={formattedPosition?.amount1 ?? ''}
                token0Address={poolInfo.token0.id}
                token1Address={poolInfo.token1.id}
                token0Symbol={poolInfo.token0.symbol}
                token1Symbol={poolInfo.token1.symbol}
              />
            </HStack>
            <HStack
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Text fontSize="14px" color="gray.4">
                APR
              </Text>
              <APRSection
                totalApr={data.apr}
                farmBaseApr={0}
                lockBonusApr={0}
              />
            </HStack>
            <Grid
              gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
              gridGap="12px"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <VaultPositionControls
                data={data}
                poolInfo={poolInfo}
                fullWidth
              />
            </Grid>
          </VStack>
        ) : (
          <>
            <Flex width="20%">
              <PoolTokensCell
                token0={poolInfo.token0}
                token1={poolInfo.token1}
                version={4}
              />
              {isOldWethPosition && (
                <Tooltip
                  className={s.transferTooltip}
                  placement="bottom-start"
                  tooltip="weETH has upgraded their token standard, which requires swapping from the old to new weETH.
Please select transfer to automatically swap and create position in the new pool."
                >
                  <Icon
                    name="infoCircle"
                    style={{ color: '#eed202', marginLeft: '8px' }}
                  />
                </Tooltip>
              )}
            </Flex>
            <Flex
              width="15%"
              sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <StrategySection vault={data.vault} />
            </Flex>
            <Flex width="10%">
              <AmountSection
                amount={formattedPosition?.balance ?? 0}
                usdAmount={formattedPosition?.balanceUsd ?? 0}
              />
            </Flex>
            <Flex width="10%">
              <CompositionSection
                amount0={formattedPosition?.amount0 ?? ''}
                amount1={formattedPosition?.amount1 ?? ''}
                token0Address={poolInfo.token0.id}
                token1Address={poolInfo.token1.id}
                token0Symbol={poolInfo.token0.symbol}
                token1Symbol={poolInfo.token1.symbol}
              />
            </Flex>
            <Flex width="5%">
              <APRSection
                totalApr={0}
                swapFeesApr={data.apr}
                farmBaseApr={0}
                lockBonusApr={0}
              />
            </Flex>
            <Flex width="25%" justifyContent="flex-end">
              <VaultPositionControls data={data} poolInfo={poolInfo} />
            </Flex>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <Box className={clsx(s.row, s.rowMobile)}>{renderContent()}</Box>
    </>
  );
};
